import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { App, Spin } from 'antd';
import PrivateRoute from 'components/PrivateRoute';
import RoleRoute from 'components/RoleRoute';
import FeatureFlagRoute from 'components/FeatureFlagRoute';
import AppLayout from 'components/layout/AppLayout';
import { CategoriesProvider } from 'contexts/categoriesContext';
import ErrorBoundary from 'components/ErrorBoundary';
// Display fonts an org can pick for menu content (FONT_OPTIONS, canvas presets).
// Each family name must match a FONT_OPTIONS value exactly, so these are the
// static packages, not the "<Name> Variable" ones. Weights cover the 300-700
// range the canvas presets and composition editor use; @font-face is lazy, so
// only the faces a menu actually renders are downloaded.
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/montserrat/300.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/600.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/unica-one/400.css';
import '@fontsource/oswald/300.css';
import '@fontsource/oswald/400.css';
import '@fontsource/oswald/500.css';
import '@fontsource/oswald/600.css';
import '@fontsource/oswald/700.css';
import '@fontsource/lato/300.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/playfair-display/600.css';
import '@fontsource/playfair-display/700.css';
// Lazy load page components
const Login = lazy(() => import('components/Login'));
const Home = lazy(() => import('components/Home'));
const MenuBoardView = lazy(() => import('components/MenuBoardView'));
const MenuListPage = lazy(() => import('pages/MenuListPage'));
const BeverageListPage = lazy(() => import('pages/BeverageListPage'));
const SpecialItemListPage = lazy(() => import('pages/SpecialItemListPage'));
const CategoryListPage = lazy(() => import('pages/CategoryListPage'));
const UserListPage = lazy(() => import('pages/admin/UserListPage'));
const UnauthorizedPage = lazy(() => import('pages/UnauthorizedPage'));
const OrgListPage = lazy(() => import('pages/admin/OrgListPage'));
const UserDetailsPage = lazy(() => import('pages/admin/UserDetailsPage'));
const OrgDetailPage = lazy(() => import('pages/admin/OrgDetailPage'));
const GlobalAdminOrgDetailPage = lazy(() => import('pages/admin/GlobalAdminOrgDetailPage'));
const MenuForm = lazy(() => import('components/forms/MenuForm'));
const ItemsListMenuView = lazy(() => import('components/ItemsListMenuView'));
const CompositionEditorPage = lazy(() => import('pages/CompositionEditorPage'));
const BeverageFormPage = lazy(() => import('pages/BeverageFormPage'));
const SpecialItemFormPage = lazy(() => import('pages/SpecialItemFormPage'));
const CategoryFormPage = lazy(() => import('pages/CategoryFormPage'));
const CanvasMenuEditorPage = lazy(() => import('pages/CanvasMenuEditorPage'));
const CanvasMenuView = lazy(() => import('components/CanvasMenuView'));

function MyApp() {
  return (
    <App>
      <BrowserRouter>
        <ErrorBoundary>
          <Suspense fallback={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <Spin size="large" />
            </div>
          }>
            <Routes>
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/login" element={<Login />} />
              <Route element={<AppLayout />}>
                <Route element={
                  <CategoriesProvider>
                    <PrivateRoute />
                  </CategoriesProvider>
                  }>
                  <Route path="/menus" element={<MenuListPage />} />
                  <Route path="/menus/:menuId/edit" element={<MenuForm />} />
                  <Route path="/menus/new" element={<MenuForm />} />
                  <Route path="/beverages" element={<BeverageListPage />} />
                  <Route path="/beverages/new" element={<BeverageFormPage />} />
                  <Route path="/beverages/:beverageId/edit" element={<BeverageFormPage />} />
                  <Route element={<FeatureFlagRoute flag="canvasMenus" />}>
                    <Route path="/menus/canvas/:menuId/edit" element={<CanvasMenuEditorPage />} />
                  </Route>
                  <Route element={<FeatureFlagRoute flag="compositionImages" />}>
                    <Route path="/beverages/:itemId/compositions" element={<CompositionEditorPage itemType="beverages" />} />
                    <Route path="/special-items/:itemId/compositions" element={<CompositionEditorPage itemType="special-items" />} />
                  </Route>
                  <Route path="/special-items" element={<SpecialItemListPage />} />
                  <Route path="/special-items/new" element={<SpecialItemFormPage />} />
                  <Route path="/special-items/:specialItemId/edit" element={<SpecialItemFormPage />} />
                  <Route path="/categories" element={<CategoryListPage />} />
                  <Route path="/categories/new" element={<CategoryFormPage />} />
                  <Route path="/categories/:categoryId/edit" element={<CategoryFormPage />} />
                  <Route path="/home" element={<Home />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['GLOBAL_ADMIN']} />}>
                  <Route path="/admin/orgs" element={<OrgListPage />} />
                  <Route path="/admin/orgs/:orgId" element={<GlobalAdminOrgDetailPage />} />
                </Route>
                <Route element={<RoleRoute allowedRoles={['ADMIN']} />}>
                  <Route path="/admin/users" element={<UserListPage />} />
                  <Route path="/admin/users/:userId" element={<UserDetailsPage />} />
                  <Route path="/admin/organization" element={<OrgDetailPage />} />
                </Route>
              </Route>
              <Route path='/:orgId/menu/:menuId/menu-board' element={<MenuBoardView />} />
              <Route path='/:orgId/menu/:menuId/items-list' element={<ItemsListMenuView />} />
              <Route path='/:orgId/menu/:menuId/canvas' element={<CanvasMenuView />} />
              <Route path='/unauthorized' element={<UnauthorizedPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
    </App>
  );
}

export default MyApp;
